import { BarChart3, ChevronDown, ChevronUp, Crown, Target, TrendingUp, Trophy, Zap } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { tournamentStatusVariant } from '../lib/badgeVariants'
import { getEntries, getProfileById } from '../services/userService'
import BracketEntryCard from './BracketEntryCard'
import TournamentDetailPanel from './TournamentDetailPanel'
import { StreakCard } from './StreaksAndBadges'
import { Avatar } from './ui/avatar'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'

export default function ProfileBody({ userId, isOwnProfile = true, showBrowseTournaments = false }) {
  const navigate = useNavigate()
  const [profile, setProfile] = useState(null)
  const [stats, setStats] = useState(null)
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [entriesLoading, setEntriesLoading] = useState(false)
  const [hasMoreEntries, setHasMoreEntries] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)

  const [expandedTournaments, setExpandedTournaments] = useState(new Set())
  const tournamentRefs = useRef({})

  // DUB-174 #1: a tournament-leaderboard click on a multi-division tournament
  // lands here with ?tournament=<id> so that tournament opens pre-expanded.
  const [searchParams] = useSearchParams()
  const expandTournamentId = searchParams.get('tournament')
  useEffect(() => {
    if (expandTournamentId) {
      setExpandedTournaments(prev => new Set(prev).add(expandTournamentId))
    }
  }, [expandTournamentId])

  // Fetch profile data
  useEffect(() => {
    const loadProfileData = async () => {
      if (!userId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)

        const profileData = await getProfileById(userId)

        if (profileData) {
          setProfile(profileData)
          setStats({
            total_points: profileData.total_points,
            potential_points: profileData.potential_points,
            global_rank: profileData.global_rank,
            entry_count: profileData.entry_count,
            groups_count: profileData.groups_count,
            prizes_count: profileData.prizes_count,
            total_players: profileData.total_players,
            champions_won: profileData.champions_won,
            total_picks: profileData.total_picks,
            accuracy: profileData.accuracy !== null && profileData.accuracy !== undefined
              ? Number(profileData.accuracy)
              : 0
          })
        }
      } catch (err) {
        console.error('Error loading profile data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadProfileData()
  }, [userId])

  // Fetch entries with pagination
  const fetchEntries = useCallback(async (reset = true) => {
    if (!userId) return

    try {
      if (reset) {
        setEntriesLoading(true)
        setCurrentPage(0)
      } else {
        setEntriesLoading(true)
      }

      const limit = 50
      const offset = reset ? 0 : currentPage * 50

      const entriesData = await getEntries({
        p_user_id: userId,
        p_limit: limit,
        p_offset: offset
      })

      const newEntries = entriesData || []

      if (reset) {
        setEntries(newEntries)
        setHasMoreEntries(newEntries.length === 50)
      } else {
        setEntries(prev => [...prev, ...newEntries])
        setHasMoreEntries(newEntries.length === 50)
      }

      if (!reset) {
        setCurrentPage(prev => prev + 1)
      }
    } catch (err) {
      console.error('Error loading entries:', err)
    } finally {
      setEntriesLoading(false)
    }
  }, [userId, currentPage])

  useEffect(() => {
    fetchEntries(true)
  }, [fetchEntries])

  const loadMoreEntries = () => {
    fetchEntries(false)
  }

  // DUB-184: once every bracket the user entered for a tournament has its
  // official results in (status 'complete'), the tournament is over even if
  // its end_date hasn't passed yet — show it as Closed instead of Live.
  const allEntriesComplete = (tournamentEntries) =>
    Array.isArray(tournamentEntries)
    && tournamentEntries.length > 0
    && tournamentEntries.every(e => e?.bracket?.status === 'complete')

  const getTournamentTimingStatus = (startDate, endDate, tournamentEntries) => {
    if (allEntriesComplete(tournamentEntries)) return 'closed'

    const now = new Date()
    const start = startDate ? new Date(startDate) : null
    const end = endDate ? new Date(endDate) : null

    if (end && now > end) return 'past'
    if (start && now < start) return 'upcoming'
    return 'live'
  }

  const entriesByTournament = entries.reduce((acc, entry) => {
    const tournamentId = entry.bracket.tournament.id
    if (!acc[tournamentId]) {
      acc[tournamentId] = {
        tournament: entry.bracket.tournament,
        entries: []
      }
    }
    acc[tournamentId].entries.push(entry)
    return acc
  }, {})

  const tournamentGroups = Object.values(entriesByTournament).sort((a, b) => {
    const statusA = getTournamentTimingStatus(a.tournament.start_date, a.tournament.end_date, a.entries)
    const statusB = getTournamentTimingStatus(b.tournament.start_date, b.tournament.end_date, b.entries)

    if (statusA === 'live' && statusB !== 'live') return -1
    if (statusA !== 'live' && statusB === 'live') return 1

    const endDateA = a.tournament.end_date ? new Date(a.tournament.end_date) : new Date(0)
    const endDateB = b.tournament.end_date ? new Date(b.tournament.end_date) : new Date(0)

    return endDateB - endDateA
  })

  const toggleTournament = (tournamentId) => {
    const isCurrentlyExpanded = expandedTournaments.has(tournamentId)

    setExpandedTournaments(prev => {
      const newSet = new Set(prev)
      if (newSet.has(tournamentId)) {
        newSet.delete(tournamentId)
      } else {
        newSet.add(tournamentId)
      }
      return newSet
    })

    if (!isCurrentlyExpanded) {
      setTimeout(() => {
        const element = tournamentRefs.current[tournamentId]
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 50)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-muted-foreground">Profile not found</p>
        </div>
      </div>
    )
  }

  const displayName = profile.full_name || 'User'

  return (
    <>
      {/* Profile Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 min-w-0">
          <Avatar
            size="lg"
            fullName={displayName}
            avatarUrl={profile?.avatar_url}
            avatarType={profile?.avatar_type}
          />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold break-words">{displayName}</h1>
          </div>
        </div>
      </div>

      {/* Player Stats */}
      <div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {/* Total Points Card */}
          <Card glassVariant="mint">
            <CardContent className="p-2.5 sm:p-3 flex flex-col h-full">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--teal-9)]" />
                <p className="text-sm sm:text-base font-bold text-[var(--color-ink)]">Points</p>
              </div>
              <p className="hidden sm:block text-xs text-muted-foreground font-body mb-2">
                {isOwnProfile ? 'You have' : `${displayName} has`} earned a total of
              </p>
              <p className="text-3xl sm:text-2xl font-bold text-[var(--color-ink)] mt-1 sm:mt-0">{(stats?.total_points || 0).toLocaleString()}</p>
              <p className="text-[11px] sm:text-xs text-muted-foreground font-body mt-auto pt-1">
                from <span className="font-bold">{(stats?.entry_count || 0).toLocaleString()}</span> brackets
              </p>
            </CardContent>
          </Card>

          {/* Global Rank Card */}
          <Card glassVariant="magenta">
            <CardContent className="p-2.5 sm:p-3 flex flex-col h-full">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--magenta-9)]" />
                <p className="text-sm sm:text-base font-bold text-[var(--magenta-12)]">Rank</p>
              </div>
              {/* DUB-144/147: three-state rank — ranked, submitted-but-unscored,
                  or no entry. Gating only on total_points wrongly showed
                  "Submit a bracket" to users who'd submitted but had no results
                  yet (e.g. pool play pre-grading). */}
              {(stats?.total_points || 0) > 0 ? (
                <>
                  <p className="hidden sm:block text-xs text-muted-foreground font-body mb-2">
                    {isOwnProfile ? 'You are' : `${displayName} is`} ranked
                  </p>
                  <p className="text-3xl sm:text-2xl font-bold text-[var(--magenta-12)] mt-1 sm:mt-0">
                    {stats?.global_rank ? `#${stats.global_rank}` : '-'}
                  </p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground font-body mt-auto pt-1">
                    of <span className="font-bold">{(stats?.total_players || 0).toLocaleString()}</span> users
                  </p>
                </>
              ) : (stats?.entry_count || 0) > 0 ? (
                <>
                  <p className="hidden sm:block text-xs text-muted-foreground font-body mb-2">
                    {isOwnProfile ? 'Your picks are' : `${displayName}'s picks are`} in
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-[var(--magenta-12)] mt-1 sm:mt-0 leading-tight">
                    Picks submitted
                  </p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground font-body mt-auto pt-1">
                    Awaiting results
                  </p>
                </>
              ) : (
                <>
                  <p className="hidden sm:block text-xs text-muted-foreground font-body mb-2">
                    {isOwnProfile ? 'You are' : `${displayName} is`} not ranked yet
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-[var(--magenta-12)] mt-1 sm:mt-0 leading-tight">
                    Submit a bracket
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Champions Card */}
          <Card glassVariant="lemon">
            <CardContent className="p-2.5 sm:p-3 flex flex-col h-full">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                <Crown className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--color-crown)]" />
                <p className="text-sm sm:text-base font-bold text-[var(--lemon-12)]">Champions</p>
              </div>
              <p className="hidden sm:block text-xs text-muted-foreground font-body mb-2">
                {isOwnProfile ? 'You have' : `${displayName} has`} correctly picked
              </p>
              <p className="text-3xl sm:text-2xl font-bold text-[var(--lemon-12)] mt-1 sm:mt-0">{(stats?.champions_won || 0).toLocaleString()}</p>
              <p className="text-[11px] sm:text-xs text-muted-foreground font-body mt-auto pt-1">
                from <span className="font-bold">{(stats?.entry_count || 0).toLocaleString()}</span> brackets
              </p>
            </CardContent>
          </Card>

          {/* Accuracy Card */}
          <Card glassVariant="blue">
            <CardContent className="p-2.5 sm:p-3 flex flex-col h-full">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                <Target className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--blue-9)]" />
                <p className="text-sm sm:text-base font-bold text-[var(--blue-12)]">Accuracy</p>
              </div>
              <p className="hidden sm:block text-xs text-muted-foreground font-body mb-2">
                {isOwnProfile ? 'You have' : `${displayName} has`} picked the winner
              </p>
              <p className="text-3xl sm:text-2xl font-bold text-[var(--blue-12)] mt-1 sm:mt-0">
                {Number.isFinite(stats?.accuracy) ? `${stats.accuracy.toFixed(2)}%` : '0.00%'}
              </p>
              <p className="text-[11px] sm:text-xs text-muted-foreground font-body mt-auto pt-1">
                of <span className="font-bold">{(stats?.total_picks || 0).toLocaleString()}</span> match-ups
              </p>
            </CardContent>
          </Card>

          {/* Streak Card (inline with other stats) */}
          <StreakCard userId={userId} />
        </div>
      </div>

      {/* Potential Points Legend */}
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-2 mb-8">
        <Zap className="h-3.5 w-3.5 text-[var(--orange-9)]" />
        <span>= potential points</span>
      </div>

      {/* Entries Section */}
      <div>
        <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
          <h2 className="text-xl font-semibold">{isOwnProfile ? 'My Tournaments' : 'Tournaments'}</h2>
          <div className="flex items-center gap-3">
            {entriesLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            )}
            {showBrowseTournaments && (
              <Button
                variant="primaryNeon"
                onClick={() => navigate('/tournaments')}
                className="w-auto"
              >
                Browse Tournaments
              </Button>
            )}
          </div>
        </div>
        {entries.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {isOwnProfile
                ? "No brackets found"
                : "This user has no brackets"}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {tournamentGroups.map((group) => {
                const isExpanded = expandedTournaments.has(group.tournament.id)
                const tournamentStatus = getTournamentTimingStatus(
                  group.tournament.start_date,
                  group.tournament.end_date,
                  group.entries
                )
                const statusLabels = { live: 'Live', upcoming: 'Upcoming', past: 'Past', closed: 'Closed' }
                const currentStatusLabel = statusLabels[tournamentStatus] || tournamentStatus
                const currentStatusVariant = tournamentStatusVariant[tournamentStatus] || 'muted'

                return (
                  <div
                    key={group.tournament.id}
                    ref={(el) => { tournamentRefs.current[group.tournament.id] = el }}
                    className="card-glass-container"
                  >
                    {/* Tournament Header - Clickable */}
                    <button
                      onClick={() => toggleTournament(group.tournament.id)}
                      className="w-full p-4 flex items-start justify-between gap-3 hover:bg-muted/50 transition-colors text-left focus:outline-none focus-visible:outline-none"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-lg font-semibold break-words">{group.tournament.name}</h3>
                          <Badge variant={currentStatusVariant}>
                            {currentStatusLabel}
                          </Badge>
                        </div>
                        {!isExpanded && (
                          <p className="text-sm text-muted-foreground">
                            View Results
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 pt-1">
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </button>

                    {/* Entries for this tournament - Collapsible */}
                    {isExpanded && (
                      <div className="p-4 pt-0">
                        {/* Own profile: the full per-tournament experience
                            (Brackets / Leaderboard / Prizes), embedded inline
                            (owner 2026-06-03). The Brackets tab already lists
                            every bracket — entered and open-but-unentered — so
                            it supersedes the old needsPicks sibling fetch.
                            Other people's profiles keep the gated entry list:
                            you can't view another player's picks until the
                            bracket locks (owner 2026-05-21). */}
                        {/* DUB-174: both own and other profiles get the inline
                            Brackets / Leaderboard / Prizes panel. For another
                            user, userId scopes the Brackets tab to THEIR entries
                            (picks stay gated until lock) while the Leaderboard
                            and Prizes tabs show the shared tournament views. */}
                        <TournamentDetailPanel
                          tournamentId={group.tournament.id}
                          userId={isOwnProfile ? undefined : userId}
                          providedEntries={isOwnProfile ? undefined : group.entries}
                          showHero={false}
                          swipeable={false}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Load More Button */}
            {hasMoreEntries && (
              <div className="mt-6 text-center">
                <Button
                  variant="outline"
                  onClick={loadMoreEntries}
                  disabled={entriesLoading}
                  className="min-w-[120px]"
                >
                  {entriesLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
